import type { Store } from 'redux';

import { store as defaultStore } from 'store/configureStore';

import { HEAP_ENVS } from 'constants/config';
import { PERMANENT_COOKIE_NAME } from 'constants/cookies';
import { SESSION_ID_NAME } from 'constants/storage';
import {
  getCountryCode,
  getCurrentLocale,
  getPaymentId,
  getPlatform,
  getRecipientId,
} from 'selectors';
import { cookies } from 'utils/cookies/cookies';
import { sessionStorage } from 'utils/storage';

type WindowWithHeap = typeof window & { heap: Heap | null };

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
let configuredStore: any = null;

interface Heap {
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (identity: string) => void;
  resetIdentity: () => void;
  addUserProperties: (properties: Record<string, unknown>) => void;
  addEventProperties: (properties: Record<string, unknown>) => void;
  removeEventProperty: (property: string) => void;
  clearEventProperties: () => void;
  appid: string;
  userId: string;
  identity: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  config: any;
}

const heapAgent = {
  configure: function (store: Store) {
    configuredStore = store;
  },

  initialize: () => {
    try {
      const store = configuredStore || defaultStore;
      const state = store.get()?.getState() ?? {};

      if (!isEnabled) {
        return;
      }

      const heap = (window as WindowWithHeap).heap;

      if (typeof heap !== 'undefined' && heap) {
        const attrs = {
          peer_tracker_id: cookies.read(PERMANENT_COOKIE_NAME),
          session_id: sessionStorage.getItem(SESSION_ID_NAME),
          source_url: window.location.href,
          browser_language: getCurrentLocale(state),
          country: getCountryCode(state),
          portal_code: getRecipientId(state),
          user_agent: navigator.userAgent,
          referrer: document.referrer,
          platform: getPlatform(state),
          paymentid: getPaymentId(state) || null,
        };

        attrs.peer_tracker_id && heap.identify(attrs.peer_tracker_id);
        heap.addUserProperties(attrs);
      }
    } catch (error) {
      console.debug(error);
    }
  },

  trackEvent: (eventName: string, metadata: Record<string, unknown>) => {
    try {
      const store = configuredStore || defaultStore;
      const state = store.get()?.getState() ?? {};

      const heap = (window as WindowWithHeap).heap;

      if (typeof heap !== 'undefined' && heap) {
        const eventBody = {
          id: cookies.read(PERMANENT_COOKIE_NAME),
          account_id: getRecipientId(state),
          ...metadata,
        };

        heap.track(eventName, eventBody);
      } else {
        console.debug('Cannot track event, heap not found');
      }
    } catch (error) {
      console.debug(error);
    }
  },
};

const isEnabled = HEAP_ENVS.includes(window.location.hostname);

export { heapAgent };
